/**
 * The dead-ButtonGroup rule (scene/gui/base_button.cpp:522-529): a `button_group` with `toggle_mode`
 * false warns, since a ButtonGroup arbitrates only toggled buttons. One rule on the base tier:
 * `applicableNodeTypeMatcher` reaches every descendant, so a new button type needs no declaration.
 */
import { descendsFromClass } from '../../../../godot/classdb/classBaseTypes.js';
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { armDiagnostic, armEmits, groundedArm } from '../../../../linter/ruleArms.js';
import { resourceSlotIsEmpty } from '../../../../linter/resourceChecker.js';
import { boolSlotValue } from '../../../../godot/variant/variantBool.js';
const arms = {
    groupWithoutToggleMode: groundedArm('button-group-without-toggle-mode', { kind: 'configuration-warning' }),
};
/**
 * Subclasses whose constructor sets toggle_mode (check_box.cpp:172, check_button.cpp:171,
 * option_button.cpp:652, menu_button.cpp:240, color_picker.cpp:2551), so absence means true.
 * doc/classes/ records each as `overrides="BaseButton" default="true"`.
 */
const TOGGLE_MODE_ON_BY_DEFAULT = new Set([
    'CheckBox',
    'CheckButton',
    'ColorPickerButton',
    'MenuButton',
    'OptionButton',
]);
function isButton(nodeType) {
    return descendsFromClass(nodeType, 'BaseButton');
}
function checkButtonGroup(context) {
    const { node } = context;
    const props = node.rawProperties;
    // An absent, empty or explicitly cleared reference is how Godot serialises
    // "no group", and `get_button_group().is_valid()` (base_button.cpp:525) is
    // false for all three.
    if (resourceSlotIsEmpty(props.button_group))
        return [];
    const toggleMode = props.toggle_mode === undefined
        ? TOGGLE_MODE_ON_BY_DEFAULT.has(node.type)
        : boolSlotValue(props.toggle_mode) === true;
    if (toggleMode)
        return [];
    return [
        armDiagnostic(arms.groupWithoutToggleMode, node, `${node.type} '${node.name}' sets 'button_group' but leaves 'toggle_mode' false. A ButtonGroup only arbitrates between toggle buttons, so the group has no effect.`),
    ];
}
const buttonGroupRule = {
    meta: {
        name: 'valid-button-group',
        description: 'Flags a ButtonGroup on a button that is not in toggle mode',
        category: 'validation',
        applicableNodeTypeMatcher: isButton,
        emits: armEmits(arms),
    },
    check: checkButtonGroup,
};
ruleRegistry.register(buttonGroupRule);
//# sourceMappingURL=linter.js.map